import { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import CreateLicenseModal from "./components/createLicenseModal";
import styles from "./License.module.css";
import licensesStore from "../../stores/licensesStore";

const getLicenseStatus = (license, today) => {
  if (!license?.exp_date) {
    return "active";
  }

  const expDate = new Date(`${license.exp_date}T00:00:00`);
  const diffDays = Math.ceil((expDate - today) / 86400000);

  if (diffDays < 0) {
    return "expired";
  }

  if (diffDays <= 30) {
    return "expiring";
  }

  return "active";
};

const LicensePage = () => {
  const {
    getLicenses,
    isLoadingLicenses,
    licenses,
    createLicense,
    deleteLicense,
  } = licensesStore;

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    getLicenses();
  }, [getLicenses]);

  const handleSubmitCreate = (values) => {
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (key === "filename" && values.filename) {
        formData.append(key, values.filename);
      } else if (key !== "filename") {
        formData.append(key, values[key]);
      }
    });

    createLicense({
      payload: formData,
      callback: () => setIsOpenModal(false),
    });
  };

  const handleOpenRemoveModal = (id) => {
    setSelectedLicenseId(id);
    setIsOpenDeleteModal(true);
  };

  const handleCloseRemoveModal = () => {
    setSelectedLicenseId(null);
    setIsOpenDeleteModal(false);
  };

  const handleSubmitDelete = () => {
    deleteLicense({ id: selectedLicenseId, callback: handleCloseRemoveModal });
  };

  const today = useMemo(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  }, []);

  const filteredLicenses = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return licenses
      .filter((license) => {
        if (!normalizedSearch) {
          return true;
        }

        const haystack = [
          license.license_description,
          license.insurance_type,
          license.policy_number,
          license.account_number,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      })
      .filter((license) => {
        if (statusFilter === "all") {
          return true;
        }

        return getLicenseStatus(license, today) === statusFilter;
      })
      .sort((a, b) => (a.exp_date || "").localeCompare(b.exp_date || ""));
  }, [licenses, searchTerm, statusFilter, today]);

  const counts = useMemo(() => {
    return licenses.reduce(
      (runningTotals, license) => {
        const status = getLicenseStatus(license, today);
        runningTotals[status] += 1;
        return runningTotals;
      },
      { active: 0, expiring: 0, expired: 0 }
    );
  }, [licenses, today]);

  return (
    !isLoadingLicenses && (
      <div className={styles.wrapper}>
        <div className="container-responsive">
          <div className={styles.toolbar}>
            <div className={styles.toolbarTitle}>
              <h4>Licenses</h4>
              <span className={styles.countPill}>{licenses.length}</span>
            </div>
            <button className={styles.blueBtn} onClick={() => setIsOpenModal(true)}>
              Add License
            </button>
          </div>

          <div className={styles.filters}>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search description, type, or policy number"
              className={styles.searchInput}
            />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div className={styles.statsRow}>
            <span className={styles.statChip}>Active {counts.active}</span>
            <span className={`${styles.statChip} ${styles.statChipWarn}`}>
              Expiring {counts.expiring}
            </span>
            <span className={`${styles.statChip} ${styles.statChipDanger}`}>
              Expired {counts.expired}
            </span>
          </div>

          <div className={styles.licensesList}>
            {filteredLicenses.length ? (
              filteredLicenses.map((license) => {
                const licenseStatus = getLicenseStatus(license, today);

                return (
                  <article key={license.id} className={styles.licenseCard}>
                    <div className={styles.licenseCardTop}>
                      <div>
                        <Link
                          to={`/license-edit/${license.id}`}
                          className={styles.licenseTitle}
                        >
                          {license.license_description}
                        </Link>
                        <div className={styles.licenseMeta}>
                          <span>{license.insurance_type}</span>
                          {license.policy_number ? (
                            <span>Policy {license.policy_number}</span>
                          ) : null}
                          {license.account_number ? (
                            <span>Acct {license.account_number}</span>
                          ) : null}
                        </div>
                      </div>
                      <span
                        className={`${styles.statusBadge} ${
                          licenseStatus === "expired"
                            ? styles.statusBadgeDanger
                            : licenseStatus === "expiring"
                            ? styles.statusBadgeWarn
                            : styles.statusBadgeActive
                        }`}
                      >
                        {licenseStatus === "expired"
                          ? "Expired"
                          : licenseStatus === "expiring"
                          ? "Expiring"
                          : "Active"}
                      </span>
                    </div>

                    <div className={styles.licenseDetails}>
                      <div>
                        <span className={styles.detailLabel}>Expiration</span>
                        <strong>{license.exp_date || "-"}</strong>
                      </div>
                      <div>
                        <span className={styles.detailLabel}>Cost</span>
                        <strong>
                          {license.cost ? `$${Number(license.cost).toFixed(2)}` : "-"}
                        </strong>
                      </div>
                      <div>
                        <span className={styles.detailLabel}>Paid</span>
                        <strong>{license.paid ? "Yes" : "No"}</strong>
                      </div>
                    </div>

                    <div className={styles.licenseActions}>
                      <Link
                        to={`/license-edit/${license.id}`}
                        className={styles.secondaryLink}
                      >
                        Open
                      </Link>
                      <button
                        className={styles.redBtn}
                        onClick={() => handleOpenRemoveModal(license.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className={styles.emptyState}>
                No licenses match the current search and filter.
              </div>
            )}
          </div>
        </div>

        <CreateLicenseModal
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          onSubmit={handleSubmitCreate}
        />

        <Modal
          isOpen={isOpenDeleteModal}
          onRequestClose={handleCloseRemoveModal}
          style={{
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: "4000",
            },
            content: {
              top: "20%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              transform: "translate(-50%, -50%)",
              zIndex: "130",
              overflow: "auto",
              maxHeight: "80%",
              width: "90%",
              maxWidth: "400px",
            },
          }}
        >
          <div className={styles.modalSubmitWrapper}>
            <span onClick={handleCloseRemoveModal} className={styles.closeBtn}>
              &#x2715;
            </span>
            <h5>Are you sure you want to delete this license?</h5>
            <div className={styles.btnBlock}>
              <button onClick={handleSubmitDelete} className={styles.blueBtn}>
                Delete
              </button>
              <button className={styles.redBtn} onClick={handleCloseRemoveModal}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </div>
    )
  );
};

export default observer(LicensePage);
